<template>
  <!-- Hero and Features Section -->
  <section
    id="features"
    class="hero-features-section min-h-screen relative overflow-hidden flex items-center"
  >
    <div class="hero-theme-overlay absolute inset-0 z-10"></div>
    <div class="hero-vignette absolute inset-0 z-20"></div>
    <div class="hero-film-grain absolute inset-0 z-20"></div>
    <div class="hero-guide-grid absolute inset-0 z-20"></div>
    <div class="absolute inset-0 z-30 pointer-events-none">
      <div
        class="hero-glow-orb-primary absolute top-1/4 left-1/4 w-[35rem] h-[35rem] rounded-full filter blur-3xl mix-blend-overlay transform -translate-x-1/2 -translate-y-1/2"
      ></div>
      <div class="hero-light-leak absolute inset-y-0 right-0 w-[42rem]"></div>
    </div>
    <div class="container mx-auto px-4 lg:px-8 hero-content-layer">
      <div
        class="flex flex-col lg:flex-row items-center justify-between gap-16"
      >
        <!-- Hero Section -->
        <div
          class="hero-copy w-full lg:w-3/5 text-center lg:text-left px-2 py-6 lg:py-8"
        >
          <p
            class="hero-kicker inline-flex items-center justify-center lg:justify-start gap-3 mb-5"
          >
            <span class="hero-kicker-mark" aria-hidden="true"></span>
            <span>PhotoScope</span>
          </p>
          <h1 class="hero-title mb-6">
            <span class="hero-title-line hero-title-line-primary">{{
              t("landing.hero.titleLine1")
            }}</span>
            <span class="hero-title-line hero-title-line-accent">{{
              t("landing.hero.titleLine2")
            }}</span>
          </h1>
          <p class="hero-subtitle hero-subtitle-text mb-10">
            {{ t("landing.hero.subtitle") }}
          </p>
          <button
            @click.prevent="navigateToUpload"
            class="theme-cta-primary theme-cta-primary--with-icon z-40"
          >
            <span class="theme-cta-primary__label">{{
              $t("landing.startFree")
            }}</span>
            <span class="theme-cta-primary__icon" aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="none">
                <path
                  d="M6.75 10h6.5"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                />
                <path
                  d="M10.5 6.75 13.75 10l-3.25 3.25"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </span>
          </button>
        </div>

        <!-- Features Section -->
        <div
          class="hero-feature-column w-full lg:w-2/5 relative"
          @mouseleave="resumeAutoRotate"
        >
          <div class="hero-feature-viewport">
            <div class="hero-feature-frame transition-all duration-500">
              <!-- Feature Content -->
              <div
                v-for="(feature, index) in features"
                :key="feature.key"
                :class="[
                  'hero-feature-slide absolute inset-0 transition-all duration-700 ease-in-out transform',
                  activeFeatureIndex === index
                    ? 'opacity-100 scale-100'
                    : 'opacity-0 scale-95',
                ]"
              >
                <div class="hero-feature-stage relative h-full w-full">
                  <div
                    class="hero-feature-stage-bar absolute left-0 right-0 top-0 z-20 flex items-center justify-end px-5 py-4"
                  >
                    <div class="hero-feature-stage-status" aria-hidden="true">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <img
                    :src="getFeatureImageSrc(feature, index)"
                    :alt="
                      t('landing.features.altTemplate', {
                        title: feature.title,
                      })
                    "
                    :loading="activeFeatureIndex === index ? 'eager' : 'lazy'"
                    :fetchpriority="
                      activeFeatureIndex === index ? 'high' : 'low'
                    "
                    decoding="async"
                    class="hero-feature-image w-full h-full object-cover"
                  />
                  <div
                    class="hero-feature-info relative z-10 h-full flex flex-col justify-end p-5 sm:p-6"
                  >
                    <div class="hero-feature-info-panel">
                      <h3 class="hero-feature-title">{{ feature.title }}</h3>
                      <p class="hero-feature-desc">{{ feature.description }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Feature Dots -->
            <div
              class="absolute left-1/2 -bottom-14 transform -translate-x-1/2 flex flex-row space-x-6 bg-black bg-opacity-30 backdrop-filter backdrop-blur-sm py-2 px-4 rounded-full"
            >
              <button
                v-for="(feature, index) in features"
                :key="feature.key"
                @click="handleDotClick(index)"
                class="w-4 h-4 rounded-full cursor-pointer transition-all duration-300 hover:scale-125 focus:outline-none group relative"
                :class="[
                  activeFeatureIndex === index
                    ? 'hero-feature-dot-active shadow-lg scale-125'
                    : 'hero-feature-dot-inactive',
                ]"
              >
                <!-- Tooltip -->
                <span
                  class="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 py-1 px-2 bg-black bg-opacity-75 text-white text-sm rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                >
                  {{ feature.title }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
@keyframes float {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.1);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}

.hero-features-section {
  isolation: isolate;
  z-index: 0;
  background: radial-gradient(
      circle at 12% 18%,
      color-mix(in srgb, var(--theme-primary) 14%, transparent) 0%,
      transparent 28%
    ),
    radial-gradient(
      circle at 86% 14%,
      color-mix(in srgb, var(--theme-accent) 16%, transparent) 0%,
      transparent 24%
    ),
    linear-gradient(
      145deg,
      color-mix(in srgb, var(--zinc-950) 96%, var(--zinc-900)) 0%,
      color-mix(in srgb, var(--zinc-900) 94%, var(--zinc-800)) 52%,
      color-mix(in srgb, var(--zinc-950) 92%, var(--theme-primary-hover) 8%)
        100%
    );
}

.hero-content-layer {
  position: relative;
  z-index: auto;
}

.hero-theme-overlay {
  pointer-events: none;
  background-image: linear-gradient(
    135deg,
    color-mix(in srgb, var(--theme-primary-hover) 12%, transparent) 0%,
    transparent 34%,
    transparent 62%,
    color-mix(in srgb, var(--theme-accent) 12%, transparent) 100%
  );
  opacity: 0.85;
}

.hero-vignette {
  pointer-events: none;
  background: radial-gradient(
    ellipse at center,
    transparent 60%,
    color-mix(in srgb, var(--zinc-950) 7%, transparent) 70%,
    color-mix(in srgb, var(--zinc-950) 55%, transparent) 100%
  );
}

.hero-film-grain {
  pointer-events: none;
  opacity: 0.16;
  mix-blend-mode: soft-light;
  background-image: repeating-linear-gradient(
      0deg,
      color-mix(in srgb, var(--zinc-50) 7%, transparent) 0,
      color-mix(in srgb, var(--zinc-50) 7%, transparent) 1px,
      transparent 1px,
      transparent 3px
    ),
    repeating-linear-gradient(
      90deg,
      color-mix(in srgb, var(--zinc-50) 4%, transparent) 0,
      color-mix(in srgb, var(--zinc-50) 4%, transparent) 1px,
      transparent 1px,
      transparent 4px
    );
}

.hero-guide-grid {
  pointer-events: none;
  opacity: 0.14;
  background-image: linear-gradient(
      to right,
      transparent 0,
      transparent calc(50% - 1px),
      color-mix(in srgb, var(--zinc-50) 11%, transparent) 50%,
      transparent calc(50% + 1px),
      transparent 100%
    ),
    linear-gradient(
      to bottom,
      transparent 0,
      transparent calc(50% - 1px),
      color-mix(in srgb, var(--zinc-50) 8%, transparent) 50%,
      transparent calc(50% + 1px),
      transparent 100%
    );
}

.hero-glow-orb-primary {
  pointer-events: none;
  background-image: linear-gradient(
    to right,
    color-mix(in srgb, var(--theme-gradient-start) 14%, transparent),
    color-mix(in srgb, var(--theme-gradient-end) 10%, transparent)
  );
  opacity: 0.52;
}

.hero-glow-orb-secondary {
  pointer-events: none;
  background-image: linear-gradient(
    to right,
    color-mix(in srgb, var(--theme-gradient-end) 8%, transparent),
    color-mix(in srgb, var(--theme-gradient-start) 16%, transparent)
  );
  opacity: 0.15;
}

.hero-light-leak {
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent 0%,
    color-mix(in srgb, var(--theme-accent) 5%, transparent) 72%,
    color-mix(in srgb, var(--theme-primary) 12%, transparent) 82%,
    color-mix(in srgb, var(--theme-accent) 10%, transparent) 100%
  );
  filter: blur(24px);
  opacity: 0.22;
}

.hero-copy {
  max-width: 44rem;
}

.hero-feature-column {
  z-index: 1;
}

.hero-feature-viewport {
  position: relative;
  width: min(100%, calc(min(78vh, 44rem) * 2 / 3));
  margin-inline: auto;
}

.hero-feature-frame {
  position: relative;
  width: 100%;
  aspect-ratio: 2 / 3;
  overflow: hidden;
  border-radius: 1.9rem;
  padding: 0.85rem;
  background: linear-gradient(
    165deg,
    color-mix(in srgb, var(--surface-overlay) 16%, transparent) 0%,
    color-mix(in srgb, var(--theme-accent) 12%, transparent) 100%
  );
  border: 1px solid color-mix(in srgb, var(--zinc-50) 10%, transparent);
  box-shadow: 0 28px 60px
      color-mix(in srgb, var(--shadow-color) 78%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 12%, transparent);
}

.hero-feature-frame::before {
  content: "";
  position: absolute;
  inset: 0.7rem;
  border-radius: 1.35rem;
  border: 1px solid color-mix(in srgb, var(--zinc-50) 8%, transparent);
  pointer-events: none;
}

.hero-feature-slide {
  padding: 0.85rem;
}

.hero-feature-stage {
  overflow: hidden;
  border-radius: 1.3rem;
  background: color-mix(
    in srgb,
    var(--zinc-950) 90%,
    var(--theme-primary-hover) 10%
  );
  box-shadow: inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 8%, transparent);
}

.hero-feature-image {
  transform: scale(1.005);
  transition: transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
}

.hero-feature-slide:hover .hero-feature-image {
  transform: scale(1.03);
}

.hero-feature-stage-bar {
  pointer-events: none;
}

.hero-feature-stage-status {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.55rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--zinc-950) 38%, transparent);
  border: 1px solid color-mix(in srgb, var(--zinc-50) 8%, transparent);
}

.hero-feature-stage-status span {
  width: 0.38rem;
  height: 0.38rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--zinc-100) 80%, var(--theme-accent));
}

.hero-feature-stage-status span:nth-child(2) {
  background: color-mix(in srgb, var(--zinc-100) 58%, var(--theme-primary));
}

.hero-feature-stage-status span:nth-child(3) {
  background: color-mix(in srgb, var(--zinc-100) 34%, transparent);
}

.hero-feature-info {
  z-index: 3;
}

.hero-feature-info-panel {
  max-width: 29rem;
  padding: 1.15rem 1.15rem 1.2rem;
  border-radius: 1.25rem;
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--zinc-950) 42%, transparent) 0%,
    color-mix(in srgb, var(--zinc-900) 62%, transparent) 100%
  );
  border: 1px solid color-mix(in srgb, var(--zinc-50) 8%, transparent);
  box-shadow: 0 14px 28px
      color-mix(in srgb, var(--shadow-color) 36%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--zinc-50) 6%, transparent);
}

.hero-feature-title {
  margin: 0;
  color: var(--zinc-50);
  font-family: "Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
  font-size: clamp(1.5rem, 2.2vw, 2rem);
  font-weight: 600;
  line-height: 1.15;
  letter-spacing: -0.02em;
  text-wrap: balance;
}

.hero-kicker {
  margin: 0 0 1.25rem;
  font-family: "Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont,
    sans-serif;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: color-mix(in srgb, var(--zinc-100) 72%, var(--theme-accent));
}

.hero-kicker-mark {
  display: inline-block;
  width: 3rem;
  height: 1px;
  background: color-mix(in srgb, var(--theme-accent) 65%, var(--zinc-50));
}

.hero-title {
  display: flex;
  flex-direction: column;
  gap: 0.12em;
  margin: 0 0 1.5rem;
  font-family: "Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, sans-serif;
  font-size: clamp(2.75rem, 4vw, 5rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.03em;
}

.hero-title-line {
  display: block;
  text-wrap: balance;
}

.hero-title-line-primary {
  color: color-mix(in srgb, var(--zinc-50) 94%, var(--theme-primary) 6%);
}

.hero-title-line-accent {
  color: color-mix(in srgb, var(--zinc-100) 76%, var(--theme-accent) 24%);
}

.hero-subtitle {
  max-width: 38rem;
  font-family: "Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont,
    sans-serif;
  font-size: clamp(1rem, 1.6vw, 1.25rem);
  font-weight: 400;
  line-height: 1.65;
  letter-spacing: 0;
}

.hero-feature-dot-active {
  background-image: linear-gradient(
    to right,
    color-mix(in srgb, var(--theme-gradient-start) 80%, var(--zinc-50)),
    color-mix(in srgb, var(--theme-gradient-end) 80%, var(--zinc-50))
  );
}

.hero-subtitle-text {
  color: color-mix(in srgb, var(--zinc-100) 82%, var(--zinc-400));
}

.hero-feature-desc {
  color: color-mix(in srgb, var(--zinc-100) 75%, var(--zinc-400));
  margin: 0.6rem 0 0;
  max-width: 32ch;
  font-family: "Inter", "SF Pro Text", -apple-system, BlinkMacSystemFont,
    sans-serif;
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.55;
  letter-spacing: 0.01em;
}

.hero-feature-dot-inactive {
  background: color-mix(in srgb, var(--zinc-50) 50%, transparent);
}

.hero-feature-dot-inactive:hover {
  background: color-mix(in srgb, var(--zinc-50) 75%, transparent);
}
</style>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { useLandingAnalytics } from "@/core/analytics/hooks/useLandingAnalytics";
import { landingFeatures as featuresSource } from "@/core/data/landingContent";
import { useUserStore } from "@/core/store/useUserStore";

const analytics = useLandingAnalytics();
const { t } = useI18n();
const route = useRoute();

const router = useRouter();
const activeFeatureIndex = ref(0);
const loadedFeatureIndexes = ref(new Set([0]));
const rotationInterval = ref(null);
const featurePreloadIdleId = ref(null);
const featurePreloadTimerId = ref(null);
const featureImagePlaceholder =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==";

const markFeatureImageLoaded = (index) => {
  if (loadedFeatureIndexes.value.has(index)) {
    return;
  }

  loadedFeatureIndexes.value = new Set([...loadedFeatureIndexes.value, index]);
};

const getNextFeatureIndex = (index = activeFeatureIndex.value) => {
  if (!features.value.length) {
    return 0;
  }

  return (index + 1) % features.value.length;
};

const cancelFeaturePreload = () => {
  if (
    featurePreloadIdleId.value !== null &&
    typeof window.cancelIdleCallback === "function"
  ) {
    window.cancelIdleCallback(featurePreloadIdleId.value);
  }

  if (featurePreloadTimerId.value !== null) {
    clearTimeout(featurePreloadTimerId.value);
  }

  featurePreloadIdleId.value = null;
  featurePreloadTimerId.value = null;
};

const scheduleNextFeaturePreload = () => {
  cancelFeaturePreload();

  const preloadNextFeature = () => {
    markFeatureImageLoaded(getNextFeatureIndex());
    featurePreloadIdleId.value = null;
    featurePreloadTimerId.value = null;
  };

  if (typeof window.requestIdleCallback === "function") {
    featurePreloadIdleId.value = window.requestIdleCallback(
      preloadNextFeature,
      { timeout: 2000 }
    );
    return;
  }

  featurePreloadTimerId.value = window.setTimeout(preloadNextFeature, 1200);
};

const setActiveFeature = (index) => {
  activeFeatureIndex.value = index;
  markFeatureImageLoaded(index);
  scheduleNextFeaturePreload();
};

const getFeatureImageSrc = (feature, index) => {
  if (!loadedFeatureIndexes.value.has(index)) {
    return featureImagePlaceholder;
  }

  return feature.backgroundImage || "/images/features/default-bg.jpg";
};

// Auto-rotate functionality
const startAutoRotate = () => {
  rotationInterval.value = setInterval(() => {
    setActiveFeature(getNextFeatureIndex());
  }, 5000);
};

const stopAutoRotate = () => {
  if (rotationInterval.value) {
    clearInterval(rotationInterval.value);
    rotationInterval.value = null;
  }
};

const resumeAutoRotate = () => {
  stopAutoRotate();
  startAutoRotate();
};

const getLandingContext = () => {
  let isLoggedIn = false;
  try {
    isLoggedIn = Boolean(useUserStore().isLoggedIn);
  } catch {
    isLoggedIn = false;
  }

  return {
    lang: route?.params?.lang,
    is_logged_in: isLoggedIn,
  };
};

const handleDotClick = (index) => {
  const previousFeature = features.value[activeFeatureIndex.value];
  setActiveFeature(index);
  stopAutoRotate();
  analytics.trackLandingOptionChanged({
    ...getLandingContext(),
    source_position: "landing_hero",
    element_name: "hero_feature_carousel",
    element_type: "carousel_dot",
    option_name: "hero_feature_slide",
    previous_value: previousFeature?.key,
    option_value: features.value[index]?.key,
    feature_key: features.value[index]?.key,
    content_id: features.value[index]?.key,
    content_type: "hero_feature",
  });
};

const navigateToUpload = async () => {
  const startedAt = Date.now();
  analytics.trackLandingCtaClicked({
    ...getLandingContext(),
    source_position: "landing_hero",
    element_name: "upload_cta",
    cta_type: "upload",
    destination_type: "route",
    destination_id: "upload",
    content_id: "upload",
    content_type: "route",
  });

  try {
    await router.push({
      name: "upload",
      params: { lang: route.params.lang || "en" },
    });
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: "landing_hero",
      element_name: "upload_cta",
      element_type: "button",
      action_status: "success",
      cta_type: "upload",
      destination_type: "route",
      destination_id: "upload",
      content_id: "upload",
      content_type: "route",
      duration_ms: Date.now() - startedAt,
    });
  } catch {
    analytics.trackLandingCtaNavigationCompleted({
      ...getLandingContext(),
      source_position: "landing_hero",
      element_name: "upload_cta",
      element_type: "button",
      action_status: "failed",
      reason_code: "router_push_failed",
      cta_type: "upload",
      destination_type: "route",
      destination_id: "upload",
      content_id: "upload",
      content_type: "route",
      duration_ms: Date.now() - startedAt,
    });
  }
};

onMounted(() => {
  startAutoRotate();
  scheduleNextFeaturePreload();
});

onUnmounted(() => {
  stopAutoRotate();
  cancelFeaturePreload();
});

const features = computed(() =>
  featuresSource.map((feature) => ({
    key: feature.key,
    title: t(feature.titleKey),
    description: t(feature.descriptionKey),
    backgroundImage: feature.backgroundImage,
  }))
);
</script>

<style scoped>
@media (max-width: 1023px) {
  .container {
    padding: 5rem 2rem;
  }

  .hero-kicker {
    justify-content: center;
  }

  .hero-subtitle {
    margin-left: auto;
    margin-right: auto;
  }

  .hero-feature-frame {
    min-height: 22rem;
    padding: 0.7rem;
  }

  .hero-feature-slide {
    padding: 0.7rem;
  }
}

@media (min-width: 1024px) {
  .container {
    max-width: 1400px;
    margin: 0 auto;
  }
}

@media (max-width: 767px) {
  .hero-title {
    font-size: clamp(2.85rem, 13vw, 4.1rem);
    line-height: 0.96;
  }

  .hero-kicker {
    letter-spacing: 0.18em;
  }

  .hero-feature-stage-bar {
    padding: 1rem 1rem 0;
  }

  .hero-feature-info-panel {
    padding: 1rem;
  }
}

/* 添加一些全局动画 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.fade-in {
  animation: fadeIn 1s ease-out;
}

/* 为特性图片添加微妙的悬停效果 */
.hero-feature-frame:hover {
  box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.3);
}
</style>
